import React, { useEffect, useRef, useState } from "react";
import "./survey-submission.scss";
import Navbar from "../../layout/header/Header";
import ActiveLink from "../../layout/active-link/ActiveLink";
import { useNavigate, useLocation, Link } from "react-router-dom";
import {
  getSurveyById,
  postSurveyResponse,
} from "../../../api/services/survey-apis";
import {
  postEmail,
  postSurveySubmissionEmail,
} from "../../../api/services/email-api";
import "bootstrap/dist/css/bootstrap.min.css";
import { toast } from "react-toastify";
import { getAssessmentById } from "../../../api/services/assessment-apis";

const SurveySubmission = () => {
  const componentRef = useRef<HTMLDivElement | null>(null);
  const [surveyId, setSurveyId] = useState<number>(0);
  const [setName, setSetName] = useState<string | null>(null);
  const [surveyDetails, setSurveyDetails] = useState<any>({});
  const [tableData, setTableData] = useState<any[]>([]); // Ensure default is an array
  const [isSubmitted, setIsSubmitted] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();
  const data = location.state;
  const [answers, setAnswers] = useState<any[]>([]);
  const [comments, setComments] = useState<{ [key: number]: string }>({});
  const [commentVisibility, setCommentVisibility] = useState<{
    [key: number]: boolean;
  }>({});
  const getRowColor = (index: any) => {
    return index % 2 === 1 ? "#ffffff" : "white";
  };

  // Fetch surveyId from location state
  useEffect(() => {
    if (location.state?.surveyId) {
      setSurveyId(location.state.surveyId);
    }
  }, [location.state]);
  const toggleComment = (quesId: number) => {
    setCommentVisibility((prevVisibility) => ({
      ...prevVisibility,
      [quesId]: !prevVisibility[quesId], // Toggle visibility
    }));
  };

  const handleCommentChange = (quesId: number, comment: string) => {
    setComments((prevComments) => ({
      ...prevComments,
      [quesId]: comment,
    }));
  };
  const fetchSurveyDetailsAndQuestions = async (surveyId: number) => {
    try {
      // Fetch survey details
      const surveyData = await getSurveyById(surveyId);
      console.log("Survey Data: ", surveyData.data);
      setSurveyDetails(surveyData.data);

      // Extract set name from survey details
      const setName = surveyData.data.setName;
      setSetName(setName);
      console.log("Fetched Set Name: ", surveyData.data.setName);

      // Fetch question list using the set name
      const questionList = await getAssessmentById(setName);
      console.log("Fetched Question List: ", questionList.data.questionList);
      setTableData(questionList.data.sectionList); // Update table data state
      setTableData(questionList.data.sectionList); // Update table data state
    } catch (error) {
      console.error("Error in fetchSurveyDetailsAndQuestions", error);
      toast.error("Failed to fetch survey details and questions");
    }
  };

  // Combined effect to fetch survey details and question list
  useEffect(() => {
    if (surveyId !== 0) {
      fetchSurveyDetailsAndQuestions(surveyId); // Ensure the API runs only once
    }
  }, [surveyId]);

  // Debugging log to track tableData updates
  useEffect(() => {
    console.log("Table Data Updated: ", tableData);
  }, [tableData]);

  const handleAnswerChange = (data: any, event: any) => {
    const selectedValue = event.target.value;

    // Handle regular options
    const selectedOption = data.options.find(
      (option: any) => option.suggestion === selectedValue
    );

    const updatedAnswers = answers.filter((ans) => ans.id !== data.quesId);
    if (selectedOption) {
      updatedAnswers.push({
        id: data.quesId,
        quesDescr: data.quesDscr,
        answer: selectedOption.answer,
        suggestion: selectedOption.suggestion,
      });
    }
    setAnswers(updatedAnswers);
  };
  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    setIsSubmitted(true);

    interface Answer {
      id: number;
      quesDescr: string;
      answer: string;
      suggestion: string;
    }

    interface Question {
      quesId: number;
      quesDscr: string;
      options: Array<{ optionId: number; answer: string; suggestion: string }>;
    }

    interface Section {
      sectionName: string;
      questionList: Question[];
    }

    interface Payload {
      surveyId: number;
      responses: Array<{
        sectionName: string;
        quesDescr: string;
        answer: string;
        suggestion: string;
      }>;
    }

    // Validate that all questions are answered
    const unansweredQuestions = tableData.flatMap((section: any) =>
      section.questionList.filter(
        (question: any) =>
          !answers.some(
            (ans) => ans.id === question.quesId && ans.answer.trim() !== ""
          )
      )
    );
    if (unansweredQuestions.length > 0) {
      // Highlight unanswered questions
      const updatedAnswers = [...answers];
      unansweredQuestions.forEach((question: any) => {
        if (!updatedAnswers.some((ans) => ans.id === question.quesId)) {
          updatedAnswers.push({
            id: question.quesId,
            quesDescr: question.quesDscr,
            answer: "",
            suggestion: "",
            isUnanswered: true, // Add a flag to mark unanswered questions
          });
        } else {
          updatedAnswers.forEach((ans) => {
            if (ans.id === question.quesId) {
              ans.isUnanswered = true; // Mark as unanswered
            }
          });
        }
      });
      setAnswers(updatedAnswers);
      // Show error message
      toast.error("Please answer all the survey questions.");
      return;
    }

    const payload: Payload = {
      surveyId: surveyId ?? 1,
      responses: tableData.flatMap((section: Section) =>
        section.questionList.map((question: Question) => {
          const answer = answers.find(
            (ans: Answer) => ans.id === question.quesId
          );
          return {
            sectionName: section.sectionName,
            quesDescr: question.quesDscr,
            answer: answer?.answer || "",
            suggestion: answer?.suggestion || "",
            additionalComment: comments[question.quesId] || "",
          };
        })
      ),
    };
    try {
      console.log("Payload: ", payload);
      const response = await postSurveyResponse(payload);
      console.log("Survey Response: ", response);

      // api call backend to generate PDF, LLM summary, and send email
      await postSurveySubmissionEmail({
        toEmail: surveyDetails.email,
        subject: "Survey Response",
        surveyDetails,
        responses: payload.responses,
      });
      toast.success(
        "Survey submitted successfully! You will receive a detailed PDF report in your email shortly."
      );
      setIsSubmitted(true);
    } catch (error) {
      console.error("Error submitting survey: ", error);
      toast.error("Error submitting survey responses");
    }
  };

  const validDurationDays = parseInt(
    process.env.REACT_APP_VALID_DURATION_DAYS || "30",
    10
  );

  let validTill = "";
  if (surveyDetails.createdDate) {
    const createdDate = new Date(surveyDetails.createdDate);
    if (!isNaN(createdDate.getTime())) {
      const validDate = new Date(createdDate);
      validDate.setDate(createdDate.getDate() + validDurationDays);
      validTill = validDate.toISOString().split("T")[0];
    }
  }

  return (
    <div className="container">
      <Navbar />
      <div className="app-pageSize">
        {!data?.flag ? (
          <ActiveLink targetLink="Survey Submission" />
        ) : (
          <div className="target-link">
            <Link to="/dashboard">Dashboard</Link>
            <p style={{ fontSize: "10px" }}>&#10095;</p>
            <p> Survey </p>
          </div>
        )}
        {/* <div> */}
        <div id="content">
          <div style={{ boxShadow: "0.5px 0.5px 0px 0px grey" }}>
            <div
              className="my-page"
              style={{ display: "flex", justifyContent: "space-between" }}
            >
              {data?.flag ? (
                <h4 className="my-page">Survey ID : {surveyId.toString()}</h4>
              ) : (
                <h4 className="my-page">Survey Submission </h4>
              )}
            </div>
            <div className="input-container-survey-submit">
              <div className="container px-5 py-3">
                {/* {data?.flag ? ( */}
                <div className="row mb-2">
                  <div className="row col-sm-6">
                    <label htmlFor="domain" className="surveyLabel col-sm-4">
                      Initiated By:{" "}
                    </label>
                    <div className="col-sm-8">
                      <span className="content">{surveyDetails.requestor}</span>
                    </div>
                  </div>
                  <div className="row col-sm-6">
                    <label htmlFor="domain" className="surveyLabel col-sm-4">
                      Initiated For:{" "}
                    </label>
                    <div className="col-sm-8">
                      <span className="content">{surveyDetails.company}</span>
                    </div>
                  </div>
                </div>
                {/* )} */}
                {/* {data?.flag ? ( */}
                <div className="row mb-2">
                  <div className="row col-sm-6">
                    <label htmlFor="domain" className="surveyLabel col-sm-4">
                      Created Date:
                    </label>
                    <div className="col-sm-8">
                      <span className="content">
                        {surveyDetails.createdDate}
                      </span>
                    </div>
                  </div>
                  <div className="row col-sm-6">
                    <label htmlFor="domain" className="surveyLabel col-sm-4">
                      Valid Till:
                    </label>
                    <div className="col-sm-8">
                      <span className="content">{validTill}</span>
                    </div>
                  </div>
                </div>
                {/* )}
                     {data?.flag ? ( */}
                <div className="row">
                  <div className="row col-sm-6">
                    <label htmlFor="domain" className="surveyLabel col-sm-4">
                      Email:
                    </label>
                    <div className="col-sm-8">
                      <span className="content">{surveyDetails.email}</span>
                    </div>
                  </div>
                  <div className="row col-sm-6">
                    <label htmlFor="domain" className="surveyLabel col-sm-4">
                      Description:
                    </label>
                    <div className="col-sm-8">
                      <span className="content">
                        {surveyDetails.surveyDscr}
                      </span>
                    </div>
                  </div>
                </div>
                {/* )} */}
              </div>
            </div>
            <br />
            <div style={{ boxShadow: "0px 0.5px 0.5px 0px grey" }}>
              <div className="table-container">
                <table className="custom-table">
                  <thead>
                    <tr>
                      <th className="survey-heading" style={{ width: "5%" }}>
                        S. No
                      </th>
                      <th className="survey-heading" style={{ width: "20%" }}>
                        Section Name
                      </th>
                      <th className="survey-heading" style={{ width: "50%" }}>
                        Question
                      </th>
                      <th className="survey-heading" style={{ width: "25%" }}>
                        Answer
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      let serialNumber = 1; // Initialize a global counter
                      return (
                        Array.isArray(tableData) &&
                        tableData.map((section: any, sectionIndex: number) =>
                          section.questionList.map(
                            (question: any, questionIndex: number) => (
                              <React.Fragment key={question.quesId}>
                                <tr
                                  className={`survey-row ${
                                    serialNumber === 6
                                      ? "page-break-before"
                                      : ""
                                  }`}
                                >
                                  <td>{serialNumber++}</td>
                                  <td>{section.sectionName}</td>
                                  <td
                                    style={{
                                      textAlign: "left",
                                      overflow: "wrap",
                                    }}
                                  >
                                    {question.quesDscr}
                                  </td>
                                  <td>
                                    <div className="answer-container">
                                      <select
                                        className={`survey-answer ${
                                          answers.some(
                                            (ans) =>
                                              ans.id === question.quesId &&
                                              ans.isUnanswered
                                          )
                                            ? "unanswered"
                                            : ""
                                        }`}
                                        onChange={(event) =>
                                          handleAnswerChange(question, event)
                                        }
                                        disabled={isSubmitted}
                                      >
                                        <option value="">select</option>
                                        {question?.options.map((op: any) => (
                                          <option
                                            key={op.optionId}
                                            value={op?.suggestion}
                                          >
                                            {op?.answer}
                                          </option>
                                        ))}
                                      </select>
                                      <i
                                        className="fas fa-comment-dots comment-icon"
                                        onClick={() =>
                                          toggleComment(question.quesId)
                                        }
                                        title="Add Comment"
                                      ></i>
                                    </div>
                                    {/* Display the text area based on visibility */}
                                    {commentVisibility[question.quesId] && (
                                      <textarea
                                        placeholder="Enter your additional comment here"
                                        value={comments[question.quesId] || ""} // Bind to the state
                                        onChange={(e) =>
                                          handleCommentChange(
                                            question.quesId,
                                            e.target.value
                                          )
                                        }
                                        disabled={isSubmitted}
                                        style={{
                                          width: "100%",
                                          minHeight: "50px",
                                          marginTop: "10px",
                                        }}
                                      />
                                    )}
                                  </td>
                                </tr>
                                {isSubmitted &&
                                  answers.map(
                                    (suggestion) =>
                                      suggestion.id === question.quesId && (
                                        <tr
                                          key={`suggestion-${question.quesId}`}
                                          className="ans-suggestion"
                                        >
                                          <td
                                            colSpan={4}
                                            style={{ textAlign: "left" }}
                                          >
                                            {suggestion.suggestion}
                                          </td>
                                        </tr>
                                      )
                                  )}
                              </React.Fragment>
                            )
                          )
                        )
                      );
                    })()}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
        {data?.flag ? (
          <div className="submit-div">
            <button
              className="submit-btn"
              onClick={() => {
                navigate("/dashboard");
              }}
            >
              Close
            </button>
          </div>
        ) : (
          !isSubmitted && (
            <div className="submit-div">
              <button
                className="submit-btn"
                // onClick={() => {
                //   navigate("/dashboard");
                // }}
                onClick={handleSubmit}
                disabled={isSubmitted} 
              >
                Submit
              </button>
            </div>
          )
        )}
      </div>
    </div>
  );
};

export default SurveySubmission;
